import type { FileAccessUrl, PresignUploadParams, PresignUploadResult } from "./types.js";

export type UploadFileParams = {
  file: Blob | File;
  /** Room the file will appear in — required for later download/preview access checks. */
  roomId?: string;
  /** Defaults to `file.name` when `file` is a `File`, otherwise `"upload"`. */
  filename?: string;
  /** Defaults to `file.type` or `application/octet-stream`. */
  mediaType?: string;
  onProgress?: (percent: number) => void;
};

export type UploadFileResult = {
  uploadId: string;
  mediaType: string;
  filename: string;
};

type UploadRest = {
  presignUpload(body: PresignUploadParams): Promise<PresignUploadResult>;
  completeUpload(uploadId: string): Promise<void>;
  abortUpload?(uploadId: string): Promise<void>;
};

export async function uploadFileToStorage(
  rest: UploadRest,
  params: UploadFileParams,
): Promise<UploadFileResult> {
  const filename = params.filename ?? (params.file instanceof File ? params.file.name : "upload");
  const mediaType = params.mediaType ?? (params.file.type || "application/octet-stream");

  const presign = await rest.presignUpload({
    filename,
    mediaType,
    sizeBytes: params.file.size,
    roomId: params.roomId,
  });

  try {
    await putWithOptionalProgress(presign.uploadUrl, params.file, mediaType, params.onProgress);
    await rest.completeUpload(presign.uploadId);
  } catch (error) {
    await rest.abortUpload?.(presign.uploadId).catch(() => undefined);
    throw error;
  }

  return {
    uploadId: presign.uploadId,
    mediaType,
    filename,
  };
}

async function putWithOptionalProgress(
  uploadUrl: string,
  file: Blob | File,
  contentType: string,
  onProgress?: (percent: number) => void,
): Promise<void> {
  if (!onProgress || typeof XMLHttpRequest === "undefined") {
    const response = await fetch(uploadUrl, {
      method: "PUT",
      body: file,
      headers: { "Content-Type": contentType },
    });
    if (!response.ok) {
      throw new Error(`Upload failed with status ${response.status}`);
    }
    return;
  }

  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", uploadUrl);
    xhr.setRequestHeader("Content-Type", contentType);
    xhr.upload.onprogress = (event) => {
      if (!event.lengthComputable) {
        return;
      }
      onProgress(Math.round((event.loaded / event.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    };
    xhr.onerror = () => reject(new Error("Upload network error"));
    xhr.send(file);
  });
}

export type { FileAccessUrl };
